import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import useValueLabels from '../hooks/useValueLabels';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Plus, X, Save } from 'lucide-react';
import FloatingActions from './FloatingActions';
import { Checkbox } from './ui/checkbox';
import { Textarea } from './ui/textarea';
import { FormDatePicker, CertificateSection } from './shared/EmployeeFormFields';
import CopyableLabel, { CopyIconButton } from './shared/CopyableLabel';

const formatDateForCopy = (d) => {
  if (!d) return '';
  const dt = d instanceof Date ? d : new Date(d);
  if (Number.isNaN(dt.getTime())) return '';
  const y = dt.getFullYear();
  const m = String(dt.getMonth() + 1).padStart(2, '0');
  const day = String(dt.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EmployeeForm = ({ employeeForm, setEmployeeForm, onSubmit, isEditing = false, showNotes = true }) => {
  const { t } = useTranslation();
  // Polish captions for the option values (Settings → General).
  const vl = useValueLabels();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings`);
      setSettings(response.data);
    } catch (error) {
      console.error('Error fetching settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDateChange = (field, date) => {
    setEmployeeForm(prev => ({ ...prev, [field]: date }));
  };

  const handleInputChange = (field, value) => {
    setEmployeeForm(prev => ({ ...prev, [field]: value }));
  };

  /**
   * Typing in the split name fields fills the required Full Name for
   * you — but only while Full Name is still empty or still matches what
   * we last composed, so a manually-worded name is never clobbered.
   */
  const handleNamePartChange = (field, value) => {
    setEmployeeForm(prev => {
      const parts = {
        first_name: prev.first_name || '',
        middle_name: prev.middle_name || '',
        last_name: prev.last_name || '',
        [field]: value,
      };
      const composedBefore = [prev.first_name, prev.middle_name, prev.last_name]
        .map(p => (p || '').trim()).filter(Boolean).join(' ');
      const composedAfter = [parts.first_name, parts.middle_name, parts.last_name]
        .map(p => (p || '').trim()).filter(Boolean).join(' ');
      const current = (prev.name || '').trim();
      const next = { ...prev, [field]: value };
      if (!current || current === composedBefore) next.name = composedAfter;
      return next;
    });
  };

  const handleEmergencyContactChange = (field, value) => {
    setEmployeeForm(prev => ({
      ...prev,
      emergency_contact: {
        ...(prev.emergency_contact || {}),
        [field]: value
      }
    }));
  };

  const handleCertificateToggle = (category, certType, checked) => {
    setEmployeeForm(prev => {
      const currentCerts = prev[category] || [];
      if (checked) {
        return {
          ...prev,
          [category]: [...currentCerts, { type: certType, expiry_date: null }]
        };
      }
      return {
        ...prev,
        [category]: currentCerts.filter(cert => cert.type !== certType)
      };
    });
  };

  const handleCertificateExpiryChange = (category, certType, date) => {
    setEmployeeForm(prev => {
      const currentCerts = prev[category] || [];
      const updatedCerts = currentCerts.map(cert =>
        cert.type === certType ? { ...cert, expiry_date: date } : cert
      );
      return {
        ...prev,
        [category]: updatedCerts
      };
    });
  };

  const isCertificateChecked = (category, certType) => {
    const certs = employeeForm[category] || [];
    return certs.some(cert => cert.type === certType);
  };

  const getCertificateExpiryDate = (category, certType) => {
    const certs = employeeForm[category] || [];
    const cert = certs.find(c => c.type === certType);
    return cert?.expiry_date || null;
  };

  // Local CertificateSectionLocal uses shared component with local handlers
  const CertificateSectionLocal = ({ title, category, certTypes }) => (
    <CertificateSection
      title={title}
      category={category}
      certTypes={certTypes}
      labelFor={vl}
      isCertificateChecked={isCertificateChecked}
      getCertificateExpiryDate={getCertificateExpiryDate}
      onToggle={handleCertificateToggle}
      onExpiryChange={handleCertificateExpiryChange}
    />
  );

  if (loading) {
    return <div className="p-4 text-center">{t('auto.proj.loading')}</div>;
  }

  return (
    <form onSubmit={onSubmit} className="space-y-6">
      <Tabs defaultValue="basic" className="space-y-4">
        <TabsList className="flex flex-wrap h-auto gap-1 p-1">
          <TabsTrigger value="basic" className="px-3 py-2" data-testid="tab-basic">{t('employees.tabs.basic')}</TabsTrigger>
          <TabsTrigger value="personal" className="px-3 py-2" data-testid="tab-personal">{t('employees.tabs.personal')}</TabsTrigger>
          <TabsTrigger value="business" className="px-3 py-2" data-testid="tab-business">{t('employees.tabs.business')}</TabsTrigger>
          <TabsTrigger value="certificates" className="px-3 py-2" data-testid="tab-certificates">{t('employees.tabs.certificates')}</TabsTrigger>
          <TabsTrigger value="visa" className="px-3 py-2" data-testid="tab-visa">{t('employees.tabs.visa')}</TabsTrigger>
        </TabsList>

        {/* Basic Information Tab */}
        <TabsContent value="basic" className="space-y-4">
          {/* Split-name fields — added 2026-07. Legacy "Full Name" is
              kept untouched (still required, still used everywhere it
              was before). Filling the split fields enables more precise
              {{employee.first_name}}/{{employee.middle_name}}/{{employee.last_name}}
              tokens in contract templates without affecting display. */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <CopyableLabel label={t('auto.proj.firstName')} value={employeeForm.first_name} testId="copy-employee-first-name" />
              <Input
                value={employeeForm.first_name || ''}
                onChange={(e) => handleNamePartChange('first_name', e.target.value)}
                data-testid="employee-first-name"
                placeholder={t('auto.proj.optional')}
              />
            </div>
            <div>
              <CopyableLabel label={t('auto.proj.middleName')} value={employeeForm.middle_name} testId="copy-employee-middle-name" />
              <Input
                value={employeeForm.middle_name || ''}
                onChange={(e) => handleNamePartChange('middle_name', e.target.value)}
                data-testid="employee-middle-name"
                placeholder={t('auto.proj.optional')}
              />
            </div>
            <div>
              <CopyableLabel label={t('auto.proj.surname')} value={employeeForm.last_name} testId="copy-employee-last-name" />
              <Input
                value={employeeForm.last_name || ''}
                onChange={(e) => handleNamePartChange('last_name', e.target.value)}
                data-testid="employee-last-name"
                placeholder={t('auto.proj.optional')}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <CopyableLabel label={t('auto.proj.fullName2')} value={employeeForm.name} testId="copy-employee-name" />
              <Input
                required
                value={employeeForm.name || ''}
                onChange={(e) => handleInputChange('name', e.target.value)}
              />
              <p className="text-[10px] text-slate-500 mt-1">
                Used everywhere across the app. Fills itself in from the fields above — edit it if you need a different wording.
              </p>
            </div>
            
            <div>
              <Label>{t('auto.proj.status')}</Label>
              <Select
                value={employeeForm.status || 'active'}
                onValueChange={(value) => handleInputChange('status', value)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">{t('auto.proj.active')}</SelectItem>
                  <SelectItem value="not_active">{t('auto.proj.notActive')}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <CopyableLabel label={t('auto.proj.email')} value={employeeForm.email} testId="copy-employee-email" />
              <Input
                type="email"
                value={employeeForm.email || ''}
                onChange={(e) => handleInputChange('email', e.target.value)}
                data-testid="employee-email"
              />
            </div>

            <div>
              <CopyableLabel label={t('auto.proj.phone')} value={employeeForm.phone} testId="copy-employee-phone" />
              <Input
                value={employeeForm.phone || ''}
                onChange={(e) => handleInputChange('phone', e.target.value)}
              />
            </div>

            <div>
              <CopyableLabel label={t('auto.proj.webUrl')} value={employeeForm.url} testId="copy-employee-url" />
              <Input
                value={employeeForm.url || ''}
                onChange={(e) => handleInputChange('url', e.target.value)}
                placeholder="https://linkedin.com/in/username"
                data-testid="employee-url-input"
              />
            </div>

            <div>
              <CopyableLabel label={t('auto.proj.department')} value={employeeForm.department} testId="copy-employee-department" />
              <Select
                value={employeeForm.department || ''}
                onValueChange={(value) => handleInputChange('department', value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder={t('auto.proj.selectDepartment')} />
                </SelectTrigger>
                <SelectContent>
                  {settings?.departments?.map(dept => (
                    <SelectItem key={dept} value={dept}>{vl(dept)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <CopyableLabel label={t('auto.proj.position1')} value={employeeForm.position_1} testId="copy-employee-position-1" />
              <Select
                value={employeeForm.position_1 || ''}
                onValueChange={(value) => handleInputChange('position_1', value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder={t('auto.proj.selectPosition')} />
                </SelectTrigger>
                <SelectContent>
                  {settings?.positions?.map(pos => (
                    <SelectItem key={pos} value={pos}>{vl(pos)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <CopyableLabel label={t('auto.proj.position2Optional')} value={employeeForm.position_2} testId="copy-employee-position-2" />
              <Select
                value={employeeForm.position_2 || 'none'}
                onValueChange={(value) => handleInputChange('position_2', value === 'none' ? '' : value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder={t('auto.proj.selectPosition')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">{t('auto.proj.none2')}</SelectItem>
                  {settings?.positions?.map(pos => (
                    <SelectItem key={pos} value={pos}>{vl(pos)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <CopyableLabel label={t('auto.proj.position3Optional')} value={employeeForm.position_3} testId="copy-employee-position-3" />
              <Select
                value={employeeForm.position_3 || 'none'}
                onValueChange={(value) => handleInputChange('position_3', value === 'none' ? '' : value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder={t('auto.proj.selectPosition')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">{t('auto.proj.none2')}</SelectItem>
                  {settings?.positions?.map(pos => (
                    <SelectItem key={pos} value={pos}>{vl(pos)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <CopyableLabel label={t('auto.proj.address')} value={employeeForm.address} testId="copy-employee-address" />
              <Input
                value={employeeForm.address || ''}
                onChange={(e) => handleInputChange('address', e.target.value)}
              />
            </div>

            <div>
              <CopyableLabel label={t('auto.proj.country')} value={employeeForm.country} testId="copy-employee-country" />
              <Select
                value={employeeForm.country || ''}
                onValueChange={(value) => handleInputChange('country', value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder={t('auto.proj.selectCountry')} />
                </SelectTrigger>
                <SelectContent>
                  {settings?.countries?.map(country => (
                    <SelectItem key={country} value={country}>{vl(country)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Education + occupation (added 2026-06) — pick-lists from
              Settings; occupation also accepts a typed-in answer. */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <CopyableLabel label={t('auto.proj.education')} value={employeeForm.education} testId="copy-employee-education" />
              <Select
                value={employeeForm.education || ''}
                onValueChange={(value) => handleInputChange('education', value)}
              >
                <SelectTrigger data-testid="employee-education">
                  <SelectValue placeholder={t('auto.proj.selectEducation')} />
                </SelectTrigger>
                <SelectContent>
                  {settings?.education_levels?.map(level => (
                    <SelectItem key={level} value={level}>{vl(level)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <CopyableLabel label={t('auto.proj.occupationTrade')} value={employeeForm.occupation} testId="copy-employee-occupation" />
              <Input
                list="employee-occupations"
                value={employeeForm.occupation || ''}
                onChange={(e) => handleInputChange('occupation', e.target.value)}
                placeholder={t('auto.proj.chooseOrTypeYourOwn')}
                data-testid="employee-occupation"
              />
              <datalist id="employee-occupations">
                {(settings?.occupations || []).map(o => <option key={o} value={o} label={vl(o)} />)}
              </datalist>
            </div>
          </div>


          {/* Hourly Rates Section */}
          <Card className="mt-6">
            <CardHeader>
              <CardTitle>{t('auto.proj.hourlyRates')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <Label>{t('auto.proj.hourlyRate1')}</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={employeeForm.hourly_rate_1 || ''}
                    onChange={(e) => handleInputChange('hourly_rate_1', parseFloat(e.target.value) || null)}
                  />
                </div>
                <div>
                  <Label>{t('auto.proj.currency1')}</Label>
                  <Select
                    value={employeeForm.currency_1 || ''}
                    onValueChange={(value) => handleInputChange('currency_1', value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={t('auto.proj.selectCurrency2')} />
                    </SelectTrigger>
                    <SelectContent>
                      {settings?.currencies?.map(curr => (
                        <SelectItem key={curr} value={curr}>{curr}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div />

                <div>
                  <Label>{t('auto.proj.hourlyRate2')}</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={employeeForm.hourly_rate_2 || ''}
                    onChange={(e) => handleInputChange('hourly_rate_2', parseFloat(e.target.value) || null)}
                  />
                </div>
                <div>
                  <Label>{t('auto.proj.currency2')}</Label>
                  <Select
                    value={employeeForm.currency_2 || ''}
                    onValueChange={(value) => handleInputChange('currency_2', value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={t('auto.proj.selectCurrency2')} />
                    </SelectTrigger>
                    <SelectContent>
                      {settings?.currencies?.map(curr => (
                        <SelectItem key={curr} value={curr}>{curr}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div />

                <div>
                  <Label>{t('auto.proj.hourlyRate3')}</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={employeeForm.hourly_rate_3 || ''}
                    onChange={(e) => handleInputChange('hourly_rate_3', parseFloat(e.target.value) || null)}
                  />
                </div>
                <div>
                  <Label>{t('auto.proj.currency3')}</Label>
                  <Select
                    value={employeeForm.currency_3 || ''}
                    onValueChange={(value) => handleInputChange('currency_3', value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={t('auto.proj.selectCurrency2')} />
                    </SelectTrigger>
                    <SelectContent>
                      {settings?.currencies?.map(curr => (
                        <SelectItem key={curr} value={curr}>{curr}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
            <FormDatePicker onDateChange={handleDateChange} field="start_date" label={t('auto.proj.startDate2')} value={employeeForm.start_date} />
            <FormDatePicker onDateChange={handleDateChange} field="hire_date" label={t('auto.proj.hireDate')} value={employeeForm.hire_date} />
          </div>

          {/* Carried over from the candidate's notes when they were recruited —
              which is where whatever they wrote in the intake form's
              "Anything else?" box ends up. */}
          {showNotes && (
            <Card className="mt-4">
              <CardHeader className="pb-3">
                <CardTitle className="text-base">{t('employees.notes.title')}</CardTitle>
                <CardDescription className="text-xs">{t('employees.notes.hint')}</CardDescription>
              </CardHeader>
              <CardContent>
                <Textarea
                  rows={5}
                  value={employeeForm.recruitment_notes || ''}
                  onChange={(e) => handleInputChange('recruitment_notes', e.target.value)}
                  placeholder={t('employees.notes.placeholder')}
                  data-testid="employee-recruitment-notes"
                />
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Personal Information Tab */}
        <TabsContent value="personal" className="space-y-4">
          {/* Row 1: Birth Date | Birth Place */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <FormDatePicker
              onDateChange={handleDateChange}
              field="birth_date"
              label={t('auto.proj.birthDate')}
              value={employeeForm.birth_date}
              labelExtras={<CopyIconButton label={t('auto.proj.birthDate')} value={employeeForm.birth_date} format={formatDateForCopy} testId="copy-employee-birth-date" />}
            />

            <div>
              <CopyableLabel label={t('auto.proj.birthPlace')} value={employeeForm.birth_place} testId="copy-employee-birth-place" />
              <Input
                value={employeeForm.birth_place || ''}
                onChange={(e) => handleInputChange('birth_place', e.target.value)}
              />
            </div>
          </div>

          {/* Row 2: Personal ID Number | (empty spacer to keep columns aligned) */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <CopyableLabel label={t('auto.proj.personalIdNumber')} value={employeeForm.personal_id_number} testId="copy-employee-personal-id" />
              <Input
                value={employeeForm.personal_id_number || ''}
                onChange={(e) => handleInputChange('personal_id_number', e.target.value)}
              />
            </div>
            <div aria-hidden="true" />
          </div>

          {/* Row 3: ID Card block (Number + Expiry stacked) | Driving Licence block (Number + Expiry stacked) */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-start">
            <div className="space-y-3" data-testid="id-card-block">
              <div>
                <CopyableLabel label={t('auto.proj.idCardNumber')} value={employeeForm.id_card_number} testId="copy-employee-id-card" />
                <Input
                  value={employeeForm.id_card_number || ''}
                  onChange={(e) => handleInputChange('id_card_number', e.target.value)}
                />
              </div>
              <FormDatePicker
                onDateChange={handleDateChange}
                field="id_card_expiry_date"
                label={t('auto.proj.idCardExpiryDate')}
                value={employeeForm.id_card_expiry_date}
                labelExtras={<CopyIconButton label={t('auto.proj.idCardExpiry')} value={employeeForm.id_card_expiry_date} format={formatDateForCopy} testId="copy-employee-id-card-expiry" />}
              />
            </div>

            <div className="space-y-3" data-testid="driving-licence-block">
              <div>
                <CopyableLabel label={t('auto.proj.drivingLicenceNumber')} value={employeeForm.driving_licence_number} testId="copy-employee-driving-licence" />
                <Input
                  value={employeeForm.driving_licence_number || ''}
                  onChange={(e) => handleInputChange('driving_licence_number', e.target.value)}
                />
              </div>
              <FormDatePicker
                onDateChange={handleDateChange}
                field="driving_licence_expiry"
                label={t('auto.proj.drivingLicenceExpiry')}
                value={employeeForm.driving_licence_expiry}
                labelExtras={<CopyIconButton label={t('auto.proj.drivingLicenceExpiry')} value={employeeForm.driving_licence_expiry} format={formatDateForCopy} testId="copy-employee-driving-licence-expiry" />}
              />
            </div>
          </div>

          {/* Row 4: Passport block (Number + Issue + Expiry stacked) | reserved spacer */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-start">
            <div className="space-y-3" data-testid="passport-block">
              <div>
                <CopyableLabel label={t('auto.proj.passportNumber')} value={employeeForm.passport_number} testId="copy-employee-passport" />
                <Input
                  value={employeeForm.passport_number || ''}
                  onChange={(e) => handleInputChange('passport_number', e.target.value)}
                />
              </div>
              <FormDatePicker
                onDateChange={handleDateChange}
                field="passport_issue_date"
                label={t('auto.proj.passportIssueDate')}
                value={employeeForm.passport_issue_date}
                labelExtras={<CopyIconButton label={t('auto.proj.passportIssueDate')} value={employeeForm.passport_issue_date} format={formatDateForCopy} testId="copy-employee-passport-issue" />}
              />
              <FormDatePicker
                onDateChange={handleDateChange}
                field="passport_expiry_date"
                label={t('auto.proj.passportExpiryDate')}
                value={employeeForm.passport_expiry_date}
                labelExtras={<CopyIconButton label={t('auto.proj.passportExpiryDate')} value={employeeForm.passport_expiry_date} format={formatDateForCopy} testId="copy-employee-passport-expiry" />}
              />
            </div>
            <div aria-hidden="true" />
          </div>

          {/* Emergency Contact Section */}
          <Card className="mt-6">
            <CardHeader>
              <CardTitle>{t('auto.proj.emergencyContact')}</CardTitle>
              <CardDescription>{t('auto.proj.personToContactInCaseOf')}</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label>{t('auto.proj.surname')}</Label>
                  <Input
                    value={employeeForm.emergency_contact?.surname || ''}
                    onChange={(e) => handleEmergencyContactChange('surname', e.target.value)}
                  />
                </div>

                <div>
                  <Label>{t('auto.proj.name')}</Label>
                  <Input
                    value={employeeForm.emergency_contact?.name || ''}
                    onChange={(e) => handleEmergencyContactChange('name', e.target.value)}
                  />
                </div>

                <div>
                  <Label>{t('auto.proj.phone')}</Label>
                  <Input
                    value={employeeForm.emergency_contact?.phone || ''}
                    onChange={(e) => handleEmergencyContactChange('phone', e.target.value)}
                  />
                </div>

                <div>
                  <Label>{t('auto.proj.email')}</Label>
                  <Input
                    type="email"
                    value={employeeForm.emergency_contact?.email || ''}
                    onChange={(e) => handleEmergencyContactChange('email', e.target.value)}
                  />
                </div>

                <div className="sm:col-span-2">
                  <Label>{t('auto.proj.relationship')}</Label>
                  <Select
                    value={employeeForm.emergency_contact?.relationship || ''}
                    onValueChange={(value) => handleEmergencyContactChange('relationship', value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={t('auto.proj.selectRelationship')} />
                    </SelectTrigger>
                    <SelectContent>
                      {settings?.relationships?.map(rel => (
                        <SelectItem key={rel} value={rel}>{vl(rel)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Business Information Tab */}
        <TabsContent value="business" className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <CopyableLabel label={t('auto.proj.businessName')} value={employeeForm.business_name} testId="copy-employee-business-name" />
              <Input
                value={employeeForm.business_name || ''}
                onChange={(e) => handleInputChange('business_name', e.target.value)}
              />
            </div>

            <div>
              <CopyableLabel label={t('auto.proj.businessAddress')} value={employeeForm.business_address} testId="copy-employee-business-address" />
              <Input
                value={employeeForm.business_address || ''}
                onChange={(e) => handleInputChange('business_address', e.target.value)}
              />
            </div>

            <div>
              <CopyableLabel label={t('auto.proj.taxIdNumber')} value={employeeForm.tax_id_number} testId="copy-employee-tax-id" />
              <Input
                value={employeeForm.tax_id_number || ''}
                onChange={(e) => handleInputChange('tax_id_number', e.target.value)}
              />
            </div>

            <div>
              <CopyableLabel label={t('auto.proj.regonNumber')} value={employeeForm.regon_number} testId="copy-employee-regon" />
              <Input
                value={employeeForm.regon_number || ''}
                onChange={(e) => handleInputChange('regon_number', e.target.value)}
              />
            </div>

            <div>
              <CopyableLabel label={t('auto.proj.pkdNumber')} value={employeeForm.pkd_number} testId="copy-employee-pkd" />
              <Select
                value={employeeForm.pkd_number || ''}
                onValueChange={(value) => handleInputChange('pkd_number', value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder={t('auto.proj.selectPkdNumber')} />
                </SelectTrigger>
                <SelectContent>
                  {settings?.pkd_numbers?.map(pkd => (
                    <SelectItem key={pkd} value={pkd}>{pkd}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="sm:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <CopyableLabel label={t('auto.proj.bankAccountPlnIban')} value={employeeForm.bank_account_pln} testId="copy-employee-bank-pln" />
                <Input
                  value={employeeForm.bank_account_pln || ''}
                  onChange={(e) => handleInputChange('bank_account_pln', e.target.value)}
                  placeholder="e.g., PL 12 3456 7890 1234 5678 9012"
                  data-testid="employee-bank-account-pln"
                />
              </div>
              <div>
                <CopyableLabel label={t('auto.proj.bankAccountEurIban')} value={employeeForm.bank_account_eur} testId="copy-employee-bank-eur" />
                <Input
                  value={employeeForm.bank_account_eur || ''}
                  onChange={(e) => handleInputChange('bank_account_eur', e.target.value)}
                  placeholder="e.g., PL 98 7654 3210 9876 5432 1098"
                  data-testid="employee-bank-account-eur"
                />
              </div>
              <div>
                <CopyableLabel label={t('auto.proj.bankAccountGbpIban')} value={employeeForm.bank_account_gbp} testId="copy-employee-bank-gbp" />
                <Input
                  value={employeeForm.bank_account_gbp || ''}
                  onChange={(e) => handleInputChange('bank_account_gbp', e.target.value)}
                  placeholder={t('auto.proj.gbpIban')}
                  data-testid="employee-bank-account-gbp"
                />
              </div>
              <div>
                <CopyableLabel label={t('auto.proj.bankAccountUsdIban')} value={employeeForm.bank_account_usd} testId="copy-employee-bank-usd" />
                <Input
                  value={employeeForm.bank_account_usd || ''}
                  onChange={(e) => handleInputChange('bank_account_usd', e.target.value)}
                  placeholder={t('auto.proj.usdIban')}
                  data-testid="employee-bank-account-usd"
                />
              </div>
              <div>
                <CopyableLabel label={t('auto.proj.swiftPln')} value={employeeForm.bank_swift_pln} testId="copy-employee-swift-pln" />
                <Input value={employeeForm.bank_swift_pln || ''} onChange={(e) => handleInputChange('bank_swift_pln', e.target.value)} placeholder={t('auto.proj.swiftBic')} data-testid="employee-swift-pln" />
              </div>
              <div>
                <CopyableLabel label={t('auto.proj.swiftEur')} value={employeeForm.bank_swift_eur} testId="copy-employee-swift-eur" />
                <Input value={employeeForm.bank_swift_eur || ''} onChange={(e) => handleInputChange('bank_swift_eur', e.target.value)} placeholder={t('auto.proj.swiftBic')} data-testid="employee-swift-eur" />
              </div>
              <div>
                <CopyableLabel label={t('auto.proj.swiftGbp')} value={employeeForm.bank_swift_gbp} testId="copy-employee-swift-gbp" />
                <Input value={employeeForm.bank_swift_gbp || ''} onChange={(e) => handleInputChange('bank_swift_gbp', e.target.value)} placeholder={t('auto.proj.swiftBic')} data-testid="employee-swift-gbp" />
              </div>
              <div>
                <CopyableLabel label={t('auto.proj.swiftUsd')} value={employeeForm.bank_swift_usd} testId="copy-employee-swift-usd" />
                <Input value={employeeForm.bank_swift_usd || ''} onChange={(e) => handleInputChange('bank_swift_usd', e.target.value)} placeholder={t('auto.proj.swiftBic')} data-testid="employee-swift-usd" />
              </div>
              <p className="sm:col-span-2 text-xs text-slate-500 -mt-1">{t('auto.proj.usedToPreFillEmployeeInvoices')}</p>
            </div>

            <div>
              <Label>{t('auto.proj.criminalRecord')}</Label>
              <Select
                value={employeeForm.criminal_record || ''}
                onValueChange={(value) => handleInputChange('criminal_record', value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder={t('auto.proj.selectStatus')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="clean">{t('auto.proj.clean')}</SelectItem>
                  <SelectItem value="pending">{t('auto.proj.pending')}</SelectItem>
                  <SelectItem value="issues">{t('auto.proj.issues')}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <FormDatePicker onDateChange={handleDateChange} field="cr_cert_issue_date" label={t('auto.proj.crCertificateIssueDate')} value={employeeForm.cr_cert_issue_date} />
            <FormDatePicker onDateChange={handleDateChange} field="contract_date" label={t('auto.proj.contractDate')} value={employeeForm.contract_date} />

            <div>
              <Label>{t('auto.proj.contractNumber')}</Label>
              <Input
                value={employeeForm.contract_number || ''}
                onChange={(e) => handleInputChange('contract_number', e.target.value)}
              />
            </div>

            {/* Per-employee invoice numbering (Phase 3 — feature 6) */}
            <div className="sm:col-span-2 mt-2 p-3 border rounded-lg bg-slate-50 space-y-3" data-testid="emp-invoice-numbering">
              <h4 className="text-sm font-medium text-slate-700">{t('auto.proj.personalInvoiceNumberingOptional')}</h4>
              <p className="text-xs text-slate-500">
                Leave the format blank to use the global Employee Invoice numbering set in Settings.
                Otherwise this employee's personal invoices will be numbered using the format below,
                with their own counter (independent from other employees).
                Placeholders: <code>{'{YYYY}'}</code> <code>{'{YY}'}</code> <code>{'{MM}'}</code>{' '}
                <code>{'{DD}'}</code> <code>{'{NNN}'}</code> <code>{'{NNNN}'}</code>
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="sm:col-span-2">
                  <Label>{t('auto.proj.format')}</Label>
                  <Input
                    value={employeeForm.invoice_number_format || ''}
                    onChange={(e) => handleInputChange('invoice_number_format', e.target.value)}
                    placeholder="e.g., RAT/{YYYY}/{NNN}"
                    data-testid="emp-invoice-format"
                  />
                </div>
                <div>
                  <Label>{t('auto.proj.nextNumber')}</Label>
                  <Input
                    type="number"
                    min="1"
                    value={employeeForm.invoice_number_next ?? ''}
                    onChange={(e) => handleInputChange(
                      'invoice_number_next',
                      e.target.value === '' ? null : parseInt(e.target.value, 10),
                    )}
                    placeholder="1"
                    data-testid="emp-invoice-next"
                  />
                </div>
              </div>
            </div>
          </div>
        </TabsContent>

        {/* Certificates Tab */}
        <TabsContent value="certificates" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <CertificateSectionLocal
              title={t('auto.proj.electricalCertificates')}
              category="electrical_certs"
              certTypes={settings?.certificate_types?.electrical}
            />

            <CertificateSectionLocal
              title={t('auto.proj.scissorLiftCertificates')}
              category="scissor_lift_certs"
              certTypes={settings?.certificate_types?.scissor_lift}
            />

            <CertificateSectionLocal
              title={t('auto.proj.boomLiftCertificates')}
              category="boom_lift_certs"
              certTypes={settings?.certificate_types?.boom_lift}
            />

            <CertificateSectionLocal
              title={t('auto.proj.oshaCertificates')}
              category="osha_certs"
              certTypes={settings?.certificate_types?.osha}
            />

            <CertificateSectionLocal
              title={t('auto.proj.healthSafetyCertificates')}
              category="health_safety_certs"
              certTypes={settings?.certificate_types?.health_safety}
            />
          </div>

          {/* Legacy A1 Certificate */}
          <Card className="mt-4">
            <CardHeader>
              <CardTitle>{t('auto.proj.a1CertificateLegacy')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label>{t('auto.proj.status')}</Label>
                  <Select
                    value={employeeForm.a1_certificate || ''}
                    onValueChange={(value) => handleInputChange('a1_certificate', value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={t('auto.proj.selectStatus')} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="yes">{t('auto.proj.yes')}</SelectItem>
                      <SelectItem value="no">{t('auto.proj.no')}</SelectItem>
                      <SelectItem value="pending">{t('auto.proj.pending')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <FormDatePicker onDateChange={handleDateChange} field="a1_expiry_date" label={t('auto.proj.a1ExpiryDate')} value={employeeForm.a1_expiry_date} />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Visa & Permits Tab */}
        <TabsContent value="visa" className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label>{t('auto.proj.euVisaPermitStatus')}</Label>
              <Select
                value={employeeForm.eu_visa || ''}
                onValueChange={(value) => handleInputChange('eu_visa', value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder={t('auto.proj.selectStatus')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="yes">{t('auto.proj.yes')}</SelectItem>
                  <SelectItem value="no">{t('auto.proj.no')}</SelectItem>
                  <SelectItem value="pending">{t('auto.proj.pending')}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>{t('auto.proj.euVisaPermitType')}</Label>
              <Select
                value={employeeForm.eu_visa_type || ''}
                onValueChange={(value) => handleInputChange('eu_visa_type', value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder={t('auto.proj.selectVisaPermitType')} />
                </SelectTrigger>
                <SelectContent>
                  {settings?.eu_visa_types?.map(type => (
                    <SelectItem key={type} value={type}>{vl(type)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <FormDatePicker onDateChange={handleDateChange} field="eu_visa_expiry_date" label={t('auto.proj.euVisaPermitExpiryDate')} value={employeeForm.eu_visa_expiry_date} />
            <div />

            <div>
              <Label>{t('auto.proj.usVisaStatus')}</Label>
              <Select
                value={employeeForm.us_visa || ''}
                onValueChange={(value) => handleInputChange('us_visa', value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder={t('auto.proj.selectStatus')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="yes">{t('auto.proj.yes')}</SelectItem>
                  <SelectItem value="no">{t('auto.proj.no')}</SelectItem>
                  <SelectItem value="pending">{t('auto.proj.pending')}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>{t('auto.proj.usVisaType')}</Label>
              <Select
                value={employeeForm.us_visa_type || ''}
                onValueChange={(value) => handleInputChange('us_visa_type', value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder={t('auto.proj.selectVisaType')} />
                </SelectTrigger>
                <SelectContent>
                  {settings?.us_visa_types?.map(type => (
                    <SelectItem key={type} value={type}>{vl(type)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <FormDatePicker onDateChange={handleDateChange} field="us_visa_expiry_date" label={t('auto.proj.usVisaExpiryDate')} value={employeeForm.us_visa_expiry_date} />
            <div />

            <div>
              <Label>{t('auto.proj.ukWorkPermitStatus')}</Label>
              <Select
                value={employeeForm.uk_work_permit || ''}
                onValueChange={(value) => handleInputChange('uk_work_permit', value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder={t('auto.proj.selectStatus')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="yes">{t('auto.proj.yes')}</SelectItem>
                  <SelectItem value="no">{t('auto.proj.no')}</SelectItem>
                  <SelectItem value="pending">{t('auto.proj.pending')}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>{t('auto.proj.ukWorkPermitType')}</Label>
              <Select
                value={employeeForm.uk_work_permit_type || ''}
                onValueChange={(value) => handleInputChange('uk_work_permit_type', value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder={t('auto.proj.selectPermitType')} />
                </SelectTrigger>
                <SelectContent>
                  {settings?.uk_work_permit_types?.map(type => (
                    <SelectItem key={type} value={type}>{vl(type)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <FormDatePicker onDateChange={handleDateChange} field="uk_work_permit_expiry_date" label={t('auto.proj.ukWorkPermitExpiryDate')} value={employeeForm.uk_work_permit_expiry_date} />
          </div>
        </TabsContent>
      </Tabs>

      <div className="flex justify-end space-x-2 pt-4 border-t">
        <Button type="submit" data-testid="employee-submit-btn">
          {isEditing ? t('auto.proj.updateEmployee') : t('auto.proj.createEmployee')}
        </Button>
      </div>

      <FloatingActions actions={[{
        icon: Save,
        label: isEditing ? t('auto.proj.updateEmployee2') : t('auto.proj.createEmployee2'),
        tone: 'primary',
        testId: 'employee-floating-save-btn',
        onClick: (e) => onSubmit(e),
      }]} />
    </form>
  );
};

export default EmployeeForm;
